import {useContext, useEffect, useRef, useState} from 'react';
import {toastInfo} from '../../../app/toast';
import {useSession} from '../../auth/hooks/useSession';
import {biotwinHub} from '../../core/hubs/biotwinHub';
import {BiotwinHubUser} from '../types';

export const useBiotwinHubUsers = (biotwinUserId: number) => {
  const session = useSession();
  const userId = Number(session?.id);
  const [biotwinHubUsers, setBiotwinHubUsers] = useState<BiotwinHubUser[]>([]);
  const biotwinHubUsersRef = useRef(biotwinHubUsers);
  const biotwinHubContext = useContext(biotwinHub.Context);

  if (biotwinHubContext == null) {
    throw new Error('BiotwinHub.Context is not defined');
  }

  useEffect(() => {
    if (biotwinHubContext?.connection?.state === 'Connected') {
      const connection = biotwinHubContext.connection;

      connection
        .invoke('GetUsers', biotwinUserId)
        .then((result) => {
          const initialUsers = (result.data ?? []).filter((u: BiotwinHubUser) => u.userId !== userId);
          biotwinHubUsersRef.current = initialUsers;
          setBiotwinHubUsers(initialUsers);
        })
        .catch((error) => {
          console.error(error);
          setBiotwinHubUsers([]);
        });

      connection.on('UserAdded', (user: BiotwinHubUser) => {
        if (user.userId !== userId) {
          setBiotwinHubUsers((prev) => [...prev, user]);
        }
      });

      connection.on('UserRemoved', (user: BiotwinHubUser) => {
        if (user.userId !== userId) {
          setBiotwinHubUsers((prev) =>
            prev.filter((u, i, a) => u.userId !== user.userId || a.findIndex((v) => v.userId === u.userId) !== i),
          );
        }
      });

      return () => {
        connection.off('UserAdded');
        connection.off('UserRemoved');
      };
    }
  }, [biotwinUserId, biotwinHubContext?.connection?.state, userId]);

  useEffect(() => {
    const addedUsers = biotwinHubUsers.filter((u) => !biotwinHubUsersRef.current.some((v) => v.userId === u.userId));
    const removedUsers = biotwinHubUsersRef.current.filter((u) => !biotwinHubUsers.some((v) => v.userId === u.userId));

    for (const user of addedUsers) {
      toastInfo(`${user.name} joined the biotwin.`);
    }

    for (const user of removedUsers) {
      toastInfo(`${user.name} left the biotwin.`);
    }

    biotwinHubUsersRef.current = biotwinHubUsers;
  }, [biotwinHubUsers]);

  return biotwinHubUsers;
};
